import React, { useCallback, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { getCurrentLanguage } from "shared/utility/lang";
import { useNavigate, useLocation } from "react-router-dom";

import { RiUserAddLine } from "react-icons/ri";

import Card from "shared/components/UIElements/Card";
import Input from "shared/components/FormElements/Input";
import Button from "shared/components/FormElements/Button";
import Link from "shared/components/FormElements/Link";
import { VALIDATOR_PASSWORD_STRONG } from "shared/util/validators";
import { useForm } from "shared/hooks/form-hook";
import { useHttpClient } from "shared/hooks/http-hook";
import { queryStringToObject } from "shared/utility/string";
import { AuthContext } from "shared/context/auth-context";
import "./Auth.css";

const AcceptInvite = () => {
  const auth = useContext(AuthContext);
  const { search } = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { isLoading, sendRequest } = useHttpClient();

  const [showPasswordNotSame, setShowPasswordNotSame] = React.useState(false);
  const [showAcceptFailed, setShowAcceptFailed] = React.useState(false);
  const [acceptFailMessage, setAcceptFailMessage] = React.useState("");
  const [token, setToken] = React.useState(null);

  const [formState, inputHandler] = useForm(
    {
      password1: {
        value: "",
        isValid: false,
      },
      password2: {
        value: "",
        isValid: false,
      },
    },
    false
  );

  const checkToken = useCallback(async () => {
    const queryParams = queryStringToObject(search, { token: "" });
    if (queryParams?.token) {
      setToken(queryParams?.token);
    } else {
      setToken(null);
      setShowAcceptFailed(true);
      setAcceptFailMessage(t("invalidInviteLink"));
    }
  }, [search, t]);

  useEffect(() => {
    checkToken();
  }, [search, checkToken]);

  const onHandleInput = () => {
    setShowPasswordNotSame(false);
    setShowAcceptFailed(false);
  };

  const acceptInviteSubmitHandler = async (event) => {
    event.preventDefault();

    if (formState.inputs.password1.value !== formState.inputs.password2.value) {
      setShowPasswordNotSame(true);
      setShowAcceptFailed(false);
      return;
    }

    let language = getCurrentLanguage();
    try {
      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/clients/accept-invite`,
        "POST",
        JSON.stringify({
          token,
          password: formState.inputs.password1.value,
          language: language,
        }),
        {
          "Content-Type": "application/json",
        }
      );

      if (responseData.code === 200) {
        const ok = await auth.login(responseData.clientId, responseData.token, null);
        if (!ok) {
          setShowAcceptFailed(true);
          setAcceptFailMessage(t("inviteAcceptFailed"));
          return;
        }
        navigate("/");
      }
    } catch (err) {
      setShowAcceptFailed(true);
      setAcceptFailMessage(err.message || t("inviteAcceptFailed"));
    }
  };

  return (
    <React.Fragment>
      <div className="auth-page-container">
        <Card className="authentication">
          <RiUserAddLine className="auth-main-icon" />
          <h2>{t("acceptInvite")}</h2>
          <hr />
          <form onSubmit={acceptInviteSubmitHandler}>
            <p style={{ textAlign: "center", marginBottom: "20px", color: "#666" }}>{t("acceptInviteDescription")}</p>

            <Input
              element="input"
              id="password1"
              type="password"
              label={t("password")}
              validators={[VALIDATOR_PASSWORD_STRONG()]}
              onInput={inputHandler}
              onChange={onHandleInput}
              disabled={isLoading || !token}
              autoComplete="new-password"
              errorText={t("passwordRequirements")}
            />

            <Input
              element="input"
              id="password2"
              type="password"
              label={t("passwordAgain")}
              validators={[VALIDATOR_PASSWORD_STRONG()]}
              onInput={inputHandler}
              onChange={onHandleInput}
              disabled={isLoading || !token}
              autoComplete="new-password"
              errorText={t("passwordRequirements")}
            />

            <Button type="submit" isLoading={isLoading} disabled={!formState.isValid || !token || isLoading}>
              {t("acceptAndContinue")}
            </Button>

            <div className="auth-bottom-container">
              <div className="auth-messages-container">
                {showPasswordNotSame && (
                  <div className="login-failed">
                    <p>{t("passwordsNotSame")}</p>
                  </div>
                )}
                {showAcceptFailed && (
                  <div className="login-failed">
                    <p>{acceptFailMessage}</p>
                  </div>
                )}
              </div>
              <div className="auth-links-container">
                <Link to="/login" styles={{ float: "left", fontWeight: "bold" }}>
                  {t("tryLogin")}
                </Link>
              </div>
            </div>
          </form>
        </Card>
      </div>
    </React.Fragment>
  );
};

export default AcceptInvite;
